import React, { useState, useRef } from 'react';
import { mockSubmissions } from '../data/mockData';
import { StatusBadge } from '../components/StatusBadge';
import { Search, Filter, Eye, Edit, X, FileText, Plus, Trash2, Upload, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { Submission, MoujaInfo } from '../types';
const emptyMoujaRow: MoujaInfo = {
  district: '',
  upazila: '',
  mouja: '',
  jlNumber: '',
  khatianStartNo: '',
  khatianEndNo: '',
  totalKhatian: '',
  totalSheet: '',
  remarks: ''
};
export const SubmissionsList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewModalData, setViewModalData] = useState<Submission | null>(null);
  const [editModalData, setEditModalData] = useState<Submission | null>(null);
  const [editFiles, setEditFiles] = useState<{
    khatianData: File | null;
    gadgetCopy: File | null;
    handoverLetter: File | null;
  }>({
    khatianData: null,
    gadgetCopy: null,
    handoverLetter: null
  });
  const xlsInputRef = useRef<HTMLInputElement>(null);
  const filteredSubmissions = mockSubmissions.filter(sub => {
    const matchesSearch = sub.id.toLowerCase().includes(searchTerm.toLowerCase()) || sub.moujaDetails && sub.moujaDetails.some(m => m.mouja.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter ? sub.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  const handleEditChange = (index: number, field: keyof MoujaInfo, value: string) => {
    if (!editModalData || !editModalData.moujaDetails) return;
    const updatedDetails = [...editModalData.moujaDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value
    };
    if (field === 'khatianStartNo' || field === 'khatianEndNo') {
      const start = parseInt(updatedDetails[index].khatianStartNo);
      const end = parseInt(updatedDetails[index].khatianEndNo);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        updatedDetails[index].totalKhatian = (end - start + 1).toString();
      }
    }
    setEditModalData({
      ...editModalData,
      moujaDetails: updatedDetails
    });
  };
  const addEditMoujaRow = () => {
    if (!editModalData) return;
    const current = editModalData.moujaDetails || [];
    setEditModalData({
      ...editModalData,
      moujaDetails: [...current, {
        ...emptyMoujaRow
      }]
    });
  };
  const removeEditMoujaRow = (index: number) => {
    if (!editModalData || !editModalData.moujaDetails || editModalData.moujaDetails.length <= 1) return;
    const updatedDetails = [...editModalData.moujaDetails];
    updatedDetails.splice(index, 1);
    setEditModalData({
      ...editModalData,
      moujaDetails: updatedDetails
    });
  };
  const handleImportXls = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editModalData || !e.target.files || e.target.files.length === 0) return;
    setTimeout(() => {
      const imported: MoujaInfo[] = [{
        district: 'Dhaka',
        upazila: 'Savar',
        mouja: 'Aminbazar',
        jlNumber: '45',
        khatianStartNo: '1',
        khatianEndNo: '500',
        totalKhatian: '500',
        totalSheet: '50',
        remarks: 'Imported'
      }, {
        district: 'Dhaka',
        upazila: 'Savar',
        mouja: 'Bhakurta',
        jlNumber: '46',
        khatianStartNo: '501',
        khatianEndNo: '1200',
        totalKhatian: '700',
        totalSheet: '70',
        remarks: 'Imported'
      }];
      const current = editModalData.moujaDetails || [];
      setEditModalData({
        ...editModalData,
        moujaDetails: [...current, ...imported]
      });
      if (xlsInputRef.current) xlsInputRef.current.value = '';
    }, 300);
  };
  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: keyof typeof editFiles) => {
    if (e.target.files && e.target.files[0]) {
      setEditFiles({
        ...editFiles,
        [type]: e.target.files[0]
      });
    }
  };
  const openEditModal = (sub: Submission) => {
    setEditModalData(JSON.parse(JSON.stringify(sub)));
    setEditFiles({
      khatianData: null,
      gadgetCopy: null,
      handoverLetter: null
    });
  };
  // ---- View Layout (read-only) ----
  const ViewFormLayout = ({
    data
  }: {
    data: Submission;
  }) => <div className="space-y-6" data-id="element-1324">
      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1325">
        <div data-id="element-1326">
          <p className="text-xs text-slate-500" data-id="element-1327">জমাদান আইডি</p>
          <p className="font-medium" data-id="element-1328">{data.id}</p>
        </div>
        <div data-id="element-1329">
          <p className="text-xs text-slate-500" data-id="element-1330">স্ট্যাটাস</p>
          <StatusBadge status={data.status} data-id="element-1331" />
        </div>
        <div data-id="element-1332">
          <p className="text-xs text-slate-500" data-id="element-1333">দাখিলকারী</p>
          <p className="font-medium" data-id="element-1334">{data.submittedBy}</p>
        </div>
        <div data-id="element-1335">
          <p className="text-xs text-slate-500" data-id="element-1336">তারিখ</p>
          <p className="font-medium" data-id="element-1337">
            {new Date(data.submittedAt).toLocaleDateString('bn-BD')}
          </p>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden" data-id="element-1338">
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 font-medium text-sm text-slate-700" data-id="element-1339">
          মৌজা ও খতিয়ান তথ্য
        </div>
        <div className="overflow-x-auto" data-id="element-1340">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-1341">
            <thead className="bg-slate-50" data-id="element-1342">
              <tr data-id="element-1343">
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1344">
                  জেলা
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1345">
                  উপজেলা
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1346">
                  মৌজা
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1347">
                  জে.এল. নং
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1348">
                  শুরু নং
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1349">
                  শেষ নং
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1350">
                  মোট
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1351">
                  শীট
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1352">
                  মন্তব্য
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-1353">
              {data.moujaDetails?.map((row, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-1354">
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1355">
                    {row.district}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1356">
                    {row.upazila}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1357">
                    {row.mouja}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1358">
                    {row.jlNumber}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1359">
                    {row.khatianStartNo}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1360">
                    {row.khatianEndNo}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1361">
                    {row.totalKhatian}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1362">
                    {row.totalSheet}
                  </td>
                  <td className="px-3 py-2 text-sm text-slate-700" data-id="element-1363">
                    {row.remarks || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md p-4 bg-slate-50" data-id="element-1364">
        <h4 className="text-sm font-medium text-slate-700 mb-3" data-id="element-1365">
          সংযুক্ত নথিপত্র
        </h4>
        <ul className="space-y-2 text-sm" data-id="element-1366">
          <li className="flex items-center text-blue-600" data-id="element-1367">
            <FileText size={16} className="mr-2" data-id="element-1368" /> {data.files.khatianData}
          </li>
          <li className="flex items-center text-red-600" data-id="element-1369">
            <FileText size={16} className="mr-2" data-id="element-1370" /> {data.files.gadgetCopy}
          </li>
          <li className="flex items-center text-red-600" data-id="element-1371">
            <FileText size={16} className="mr-2" data-id="element-1372" /> {data.files.handoverLetter}
          </li>
        </ul>
      </div>
    </div>;
  // ---- Edit Layout ----
  const EditFormLayout = ({
    data
  }: {
    data: Submission;
  }) => <div className="space-y-6" data-id="element-1373">
      <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1374">
        <div data-id="element-1375">
          <p className="text-xs text-slate-500" data-id="element-1376">জমাদান আইডি</p>
          <p className="font-medium" data-id="element-1377">{data.id}</p>
        </div>
        <div data-id="element-1378">
          <p className="text-xs text-slate-500" data-id="element-1379">স্ট্যাটাস</p>
          <StatusBadge status={data.status} data-id="element-1380" />
        </div>
        <div data-id="element-1381">
          <p className="text-xs text-slate-500" data-id="element-1382">দাখিলকারী</p>
          <p className="font-medium" data-id="element-1383">{data.submittedBy}</p>
        </div>
        <div data-id="element-1384">
          <p className="text-xs text-slate-500" data-id="element-1385">তারিখ</p>
          <p className="font-medium" data-id="element-1386">
            {new Date(data.submittedAt).toLocaleDateString('bn-BD')}
          </p>
        </div>
      </div>

      {/* Mouja Table with Add/Remove/Import */}
      <div className="border border-slate-200 rounded-md overflow-hidden" data-id="element-1387">
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex justify-between items-center" data-id="element-1388">
          <span className="font-medium text-sm text-slate-700" data-id="element-1389">
            মৌজা ও খতিয়ান তথ্য
          </span>
          <div className="flex space-x-2" data-id="element-1390">
            <input type="file" accept=".xls,.xlsx" className="hidden" ref={xlsInputRef} onChange={handleImportXls} data-id="element-1391" />
            <button type="button" onClick={() => xlsInputRef.current?.click()} className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded flex items-center hover:bg-green-100 transition-colors" data-id="element-1392">
              <FileSpreadsheet size={14} className="mr-1" data-id="element-1393" /> এক্সেল আমদানি
            </button>
            <button type="button" onClick={addEditMoujaRow} className="text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 px-2.5 py-1 rounded flex items-center hover:bg-teal-100 transition-colors" data-id="element-1394">
              <Plus size={14} className="mr-1" data-id="element-1395" /> সারি যোগ করুন
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-1396">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-1397">
            <thead className="bg-slate-50" data-id="element-1398">
              <tr data-id="element-1399">
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1400">
                  জেলা
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1401">
                  উপজেলা
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1402">
                  মৌজা
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1403">
                  জে.এল. নং
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1404">
                  শুরু নং
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1405">
                  শেষ নং
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1406">
                  মোট
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1407">
                  শীট
                </th>
                <th className="px-2 py-2 text-left text-xs font-medium text-slate-500" data-id="element-1408">
                  মন্তব্য
                </th>
                <th className="px-2 py-2 text-center text-xs font-medium text-slate-500 w-10" data-id="element-1409"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-1410">
              {data.moujaDetails?.map((row, index) => <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} data-id="element-1411">
                  <td className="px-1 py-1.5" data-id="element-1412">
                    <select value={row.district} onChange={e => handleEditChange(index, 'district', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1413">
                      <option value="" data-id="element-1414">নির্বাচন করুন</option>
                      <option value="Dhaka" data-id="element-1415">ঢাকা</option>
                      <option value="Gazipur" data-id="element-1416">গাজীপুর</option>
                      <option value="Narayanganj" data-id="element-1417">নারায়ণগঞ্জ</option>
                      <option value="Chittagong" data-id="element-1418">চট্টগ্রাম</option>
                      <option value="Sylhet" data-id="element-1419">সিলেট</option>
                    </select>
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1420">
                    <select value={row.upazila} onChange={e => handleEditChange(index, 'upazila', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1421">
                      <option value="" data-id="element-1422">নির্বাচন করুন</option>
                      <option value="Savar" data-id="element-1423">সাভার</option>
                      <option value="Tongi" data-id="element-1424">টঙ্গী</option>
                      <option value="Rupganj" data-id="element-1425">রূপগঞ্জ</option>
                      <option value="Hathazari" data-id="element-1426">হাটহাজারী</option>
                      <option value="Golapganj" data-id="element-1427">গোলাপগঞ্জ</option>
                    </select>
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1428">
                    <input type="text" value={row.mouja} onChange={e => handleEditChange(index, 'mouja', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" placeholder="মৌজা" data-id="element-1429" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1430">
                    <input type="text" value={row.jlNumber} onChange={e => handleEditChange(index, 'jlNumber', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1431" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1432">
                    <input type="text" value={row.khatianStartNo} onChange={e => handleEditChange(index, 'khatianStartNo', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1433" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1434">
                    <input type="text" value={row.khatianEndNo} onChange={e => handleEditChange(index, 'khatianEndNo', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1435" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1436">
                    <input type="text" value={row.totalKhatian} onChange={e => handleEditChange(index, 'totalKhatian', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm bg-slate-50 focus:ring-1 focus:ring-slate-900" readOnly data-id="element-1437" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1438">
                    <input type="text" value={row.totalSheet} onChange={e => handleEditChange(index, 'totalSheet', e.target.value)} className="w-14 border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" data-id="element-1439" />
                  </td>
                  <td className="px-1 py-1.5" data-id="element-1440">
                    <input type="text" value={row.remarks} onChange={e => handleEditChange(index, 'remarks', e.target.value)} className="w-full border border-slate-300 rounded px-1.5 py-1 text-sm focus:ring-1 focus:ring-slate-900" placeholder="মন্তব্য" data-id="element-1441" />
                  </td>
                  <td className="px-1 py-1.5 text-center" data-id="element-1442">
                    <button type="button" onClick={() => removeEditMoujaRow(index)} disabled={(data.moujaDetails?.length || 0) <= 1} className={`p-1 rounded ${(data.moujaDetails?.length || 0) <= 1 ? 'text-slate-300 cursor-not-allowed' : 'text-red-500 hover:bg-red-50'}`} data-id="element-1443">
                      <Trash2 size={14} data-id="element-1444" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Document Uploads — Replace Files */}
      <div className="border border-slate-200 rounded-md overflow-hidden" data-id="element-1445">
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 font-medium text-sm text-slate-700" data-id="element-1446">
          নথি আপলোড
        </div>
        <div className="p-4 space-y-4" data-id="element-1447">
          {/* Khatian Data File */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1448">
            <div className="flex items-center" data-id="element-1449">
              <FileText className="text-blue-500 mr-3" size={20} data-id="element-1450" />
              <div data-id="element-1451">
                <p className="text-sm font-medium text-slate-800" data-id="element-1452">
                  খতিয়ান ডেটা ফাইল
                </p>
                <p className="text-xs text-slate-500" data-id="element-1453">
                  {editFiles.khatianData ? editFiles.khatianData.name : data.files.khatianData}
                  {editFiles.khatianData && <span className="ml-2 text-teal-600 font-medium" data-id="element-1454">
                      (নতুন)
                    </span>}
                </p>
              </div>
            </div>
            <label className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded cursor-pointer hover:bg-blue-100 flex items-center transition-colors" data-id="element-1455">
              <RefreshCw size={12} className="mr-1" data-id="element-1456" /> প্রতিস্থাপন
              <input type="file" className="hidden" accept=".mdf,.ldf,.csv,.xlsx" onChange={e => handleEditFileChange(e, 'khatianData')} data-id="element-1457" />
            </label>
          </div>

          {/* Gadget Copy/Case Copy */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1458">
            <div className="flex items-center" data-id="element-1459">
              <FileText className="text-red-500 mr-3" size={20} data-id="element-1460" />
              <div data-id="element-1461">
                <p className="text-sm font-medium text-slate-800" data-id="element-1462">
                  গেজেট কপি/কেস কপি
                </p>
                <p className="text-xs text-slate-500" data-id="element-1463">
                  {editFiles.gadgetCopy ? editFiles.gadgetCopy.name : data.files.gadgetCopy}
                  {editFiles.gadgetCopy && <span className="ml-2 text-teal-600 font-medium" data-id="element-1464">
                      (নতুন)
                    </span>}
                </p>
              </div>
            </div>
            <label className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded cursor-pointer hover:bg-blue-100 flex items-center transition-colors" data-id="element-1465">
              <RefreshCw size={12} className="mr-1" data-id="element-1466" /> প্রতিস্থাপন
              <input type="file" className="hidden" accept=".pdf" onChange={e => handleEditFileChange(e, 'gadgetCopy')} data-id="element-1467" />
            </label>
          </div>

          {/* Handover Letter/Correction Request Letter */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-1468">
            <div className="flex items-center" data-id="element-1469">
              <FileText className="text-red-500 mr-3" size={20} data-id="element-1470" />
              <div data-id="element-1471">
                <p className="text-sm font-medium text-slate-800" data-id="element-1472">
                  হস্তান্তর পত্র/সংশোধন পত্র
                </p>
                <p className="text-xs text-slate-500" data-id="element-1473">
                  {editFiles.handoverLetter ? editFiles.handoverLetter.name : data.files.handoverLetter}
                  {editFiles.handoverLetter && <span className="ml-2 text-teal-600 font-medium" data-id="element-1474">
                      (নতুন)
                    </span>}
                </p>
              </div>
            </div>
            <label className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded cursor-pointer hover:bg-blue-100 flex items-center transition-colors" data-id="element-1475">
              <RefreshCw size={12} className="mr-1" data-id="element-1476" /> প্রতিস্থাপন
              <input type="file" className="hidden" accept=".pdf" onChange={e => handleEditFileChange(e, 'handoverLetter')} data-id="element-1477" />
            </label>
          </div>
        </div>
      </div>
    </div>;
  return <div className="max-w-7xl mx-auto" data-id="element-1478">
      <div className="mb-6" data-id="element-1479">
        <h1 className="text-2xl font-bold text-slate-900" data-id="element-1480">সকল জমাদান</h1>
        <p className="text-slate-500" data-id="element-1481">
          সিস্টেম জুড়ে সকল তথ্য জমাদান দেখুন ও পরিচালনা করুন
        </p>
      </div>

      <div className="card overflow-hidden flex flex-col h-[calc(100vh-12rem)]" data-id="element-1482">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex space-x-4" data-id="element-1483">
          <div className="relative flex-1 max-w-md" data-id="element-1484">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} data-id="element-1485" />
            <input type="text" placeholder="আইডি বা মৌজা দিয়ে অনুসন্ধান..." className="input-field pl-10 text-sm" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1486" />
          </div>
          <select className="input-field w-48 text-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-1487">
            <option value="" data-id="element-1488">সকল স্ট্যাটাস</option>
            <option value="pending_validation" data-id="element-1489">যাচাই অপেক্ষমাণ</option>
            <option value="processing" data-id="element-1490">প্রক্রিয়াকরণ</option>
            <option value="ready_for_approval" data-id="element-1491">অনুমোদনের জন্য প্রস্তুত</option>
            <option value="published" data-id="element-1492">প্রকাশিত</option>
            <option value="rejected" data-id="element-1493">প্রত্যাখ্যাত</option>
          </select>
          <button className="btn-secondary flex items-center text-sm" data-id="element-1494">
            <Filter size={16} className="mr-2" data-id="element-1495" /> আরও ফিল্টার
          </button>
        </div>

        <div className="flex-1 overflow-auto" data-id="element-1496">
          <table className="min-w-full divide-y divide-slate-200" data-id="element-1497">
            <thead className="bg-slate-50 sticky top-0 z-10" data-id="element-1498">
              <tr data-id="element-1499">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1500">
                  আইডি
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1501">
                  মৌজা (প্রাথমিক)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1502">
                  মোট খতিয়ান
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1503">
                  দাখিলকারী
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1504">
                  তারিখ
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-1505">
                  স্ট্যাটাস
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase" data-id="element-1506">
                  কার্যক্রম
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200" data-id="element-1507">
              {filteredSubmissions.map(sub => {
              const primaryMouja = sub.moujaDetails?.[0] || {
                mouja: sub.mouja,
                district: sub.district,
                upazila: sub.upazila
              };
              const totalKhatians = sub.moujaDetails ? sub.moujaDetails.reduce((acc, curr) => acc + (parseInt(curr.totalKhatian) || 0), 0) : sub.khatianCount;
              return <tr key={sub.id} className="hover:bg-slate-50" data-id="element-1508">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900" data-id="element-1509">
                      {sub.id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600" data-id="element-1510">
                      {primaryMouja.mouja}, {primaryMouja.upazila}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-1511">
                      {totalKhatians}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-1512">
                      {sub.submittedBy}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500" data-id="element-1513">
                      {new Date(sub.submittedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-id="element-1514">
                      <StatusBadge status={sub.status} data-id="element-1515" />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2" data-id="element-1516">
                      <button onClick={() => setViewModalData(sub)} className="text-blue-600 hover:text-blue-900 p-1 bg-blue-50 rounded" title="দেখুন" data-id="element-1517">
                        <Eye size={16} data-id="element-1518" />
                      </button>
                      <button onClick={() => openEditModal(sub)} className="text-amber-600 hover:text-amber-900 p-1 bg-amber-50 rounded" title="সম্পাদনা" data-id="element-1519">
                        <Edit size={16} data-id="element-1520" />
                      </button>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Modal */}
      {viewModalData && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1521">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-1522">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-lg" data-id="element-1523">
              <h2 className="text-lg font-bold text-slate-800" data-id="element-1524">
                জমাদান দেখুন — {viewModalData.id}
              </h2>
              <button onClick={() => setViewModalData(null)} className="text-slate-400 hover:text-slate-600" data-id="element-1525">
                <X size={20} data-id="element-1526" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1" data-id="element-1527">
              <ViewFormLayout data={viewModalData} data-id="element-1528" />
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end" data-id="element-1529">
              <button onClick={() => setViewModalData(null)} className="btn-secondary" data-id="element-1530">
                বন্ধ করুন
              </button>
            </div>
          </div>
        </div>}

      {/* Edit Modal */}
      {editModalData && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1531">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col" data-id="element-1532">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-amber-50 rounded-t-lg" data-id="element-1533">
              <h2 className="text-lg font-bold text-slate-800 flex items-center" data-id="element-1534">
                <Edit size={18} className="mr-2 text-amber-600" data-id="element-1535" /> জমাদান
                সম্পাদনা — {editModalData.id}
              </h2>
              <button onClick={() => setEditModalData(null)} className="text-slate-400 hover:text-slate-600" data-id="element-1536">
                <X size={20} data-id="element-1537" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1" data-id="element-1538">
              <EditFormLayout data={editModalData} data-id="element-1539" />
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end space-x-3" data-id="element-1540">
              <button onClick={() => setEditModalData(null)} className="btn-secondary" data-id="element-1541">
                বাতিল
              </button>
              <button onClick={() => setEditModalData(null)} className="btn-primary flex items-center" data-id="element-1542">
                <Upload size={16} className="mr-2" data-id="element-1543" /> পরিবর্তন সংরক্ষণ করুন
              </button>
            </div>
          </div>
        </div>}
    </div>;
};